import { ReactElement, useState } from 'react';
import userStore from '@/stores/user.store';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { chatStore } from '@/stores/chat.store';
import { IUser } from '@/utils/types/User';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { ApiRequest } from '@/utils/axios';
import { IJackpotGame } from '@/utils/types/IJackpotHistoryGame';
import { ICoinflipGame } from '@/utils/types/ICoinflipGame';

export interface IAdminButtons {
    winnerSide?: boolean;
    jackpotWinner?: boolean;
    battleWinner?: boolean;
}

const Item = ({
    onClick,
    icon,
    label,
}: {
    onClick: () => void;
    icon: ReactElement;
    label: string;
}) => (
    <button
        onClick={onClick}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-fl text-[12.5px] font-medium text-text_2 hover:bg-layer_3 hover:text-text_main transition-colors text-left"
    >
        {icon}
        <span>{label}</span>
    </button>
);

const CupIcon = () => (
    <svg
        viewBox="0 0 24 24"
        className="w-3.5 h-3.5 stroke-accent_5 fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
    >
        <path d="M8 21h8M12 17v4M17 4h3v3a4 4 0 0 1-4 4M7 4H4v3a4 4 0 0 0 4 4M7 4v6a5 5 0 0 0 10 0V4H7z" />
    </svg>
);

const GhostIcon = () => (
    <svg
        viewBox="0 0 24 24"
        className="w-3.5 h-3.5 stroke-accent_3 fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
    >
        <path d="M12 3a7 7 0 0 0-7 7v9l3-2 2 2 2-2 2 2 3-2v-7a7 7 0 0 0-5-7z" />
        <circle cx="9.5" cy="11" r="0.6" fill="currentColor" />
        <circle cx="14.5" cy="11" r="0.6" fill="currentColor" />
    </svg>
);

interface Props {
    children: React.ReactNode;
    user: IUser;
    getChatHistory?: () => Promise<void>;
    messageId?: number;
    winnerSide?: boolean;
    game?: IJackpotGame | ICoinflipGame;
    teamId?: number;
    buttons?: IAdminButtons;
}

const AdminWrapper = ({ children, user, game, buttons, teamId }: Props) => {
    const profile = userStore((state) => state.user);
    const setSendFrom = chatStore((state) => state.setSendFrom);
    const [visible, setVisible] = useState(false);

    async function setWinner() {
        if (game) {
            const {
                data: { success },
            } = await ApiRequest.post(
                `/admin/${'value' in game ? 'jackpot' : 'coinflip'}/setWinner`,
                {
                    userId: user.id,
                    gameId: game.id,
                }
            );
            if (success) {
                notify(
                    'success',
                    <SuccessIcon />,
                    <NotifyWrapper title={'Success'} text={`User: ${user.name} will win`} />,
                    2000
                );
            }
            setVisible(false);
        }
    }

    async function setBattleWinner() {
        if (teamId) {
            const {
                data: { success },
            } = await ApiRequest.post(`/admin/casebattle/setWinner`, {
                userId: user.id,
                teamId,
                deviationAmountUsd: 0.05,
            });
            if (success) {
                notify(
                    'success',
                    <SuccessIcon />,
                    <NotifyWrapper title={'Success'} text={`User: ${user.name} will win`} />,
                    2000
                );
            }
            setVisible(false);
        }
    }

    async function setShadow() {
        setSendFrom(user.id);

        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title={'Success'} text={`Now you can chat as ${user.name}`} />,
            2000
        );
        setVisible(false);
    }

    return (
        <Popover
            isOpen={visible && profile.status != 'USER'}
            positions={['right']}
            padding={10}
            reposition={false}
            onClickOutside={() => setVisible(false)}
            content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                    position={position}
                    childRect={childRect}
                    arrowColor="rgba(20,22,28,0.96)"
                    arrowSize={7}
                    arrowStyle={{ opacity: 1 }}
                    className=""
                    arrowClassName=""
                    popoverRect={popoverRect}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="rounded-fl-md border border-stroke_strong p-1 min-w-[160px]"
                        style={{
                            background: 'rgba(20,22,28,0.96)',
                            backdropFilter: 'blur(20px) saturate(140%)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                        }}
                    >
                        {buttons?.jackpotWinner && (
                            <Item onClick={setWinner} label="Set as winner" icon={<CupIcon />} />
                        )}
                        <Item onClick={setShadow} label="Chat shadow" icon={<GhostIcon />} />
                        {buttons?.winnerSide && (
                            <Item
                                onClick={setWinner}
                                label="Set as winner side"
                                icon={<CupIcon />}
                            />
                        )}
                        {buttons?.battleWinner && (
                            <Item
                                onClick={setBattleWinner}
                                label="Set as winner"
                                icon={<CupIcon />}
                            />
                        )}
                    </div>
                </ArrowContainer>
            )}
        >
            <div className="shrink-0" onClick={() => setVisible(!visible)}>
                {children}
            </div>
        </Popover>
    );
};

export default AdminWrapper;
